import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileDown, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { requestCatalogue } from "@/lib/api";
import { REGIONS } from "@/lib/site";

const EMPTY = { name: "", email: "", company: "", country: "" };

export const CatalogueGate = ({ catalogue, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (catalogue) setForm(EMPTY); }, [catalogue]);

  const mutation = useMutation({
    mutationFn: requestCatalogue,
    onSuccess: (res) => {
      if (res.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
        toast.success("Download started", { description: `${res.title} is opening in a new tab.` });
      } else {
        toast.success("Request received", { description: `We will email the ${res.title} PDF to you shortly.` });
      }
      onClose();
    },
    onError: (e) => toast.error("Could not process the request", { description: e?.response?.data?.detail?.[0]?.msg || "Please check your details and try again." }),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    mutation.mutate({ ...form, company: form.company || null, catalogue_slug: catalogue.slug });
  };

  return (
    <Dialog open={!!catalogue} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="catalogue-gate-modal" className="max-w-md rounded-[1.5rem] border-slate-200 bg-white p-7 sm:p-9" data-lenis-prevent>
        <p className="eyebrow mb-3 flex items-center gap-2"><Lock size={11} /> Catalogue access</p>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight text-[var(--navy)]" data-testid="catalogue-gate-title">
          {catalogue?.title}
        </DialogTitle>
        <DialogDescription className="mt-2 text-sm leading-relaxed text-[var(--muted)]">
          Tell us who you are and the {catalogue?.pages}-page PDF opens instantly. We only use this to follow up with pricing.
        </DialogDescription>
        <form onSubmit={submit} data-testid="catalogue-gate-form" className="mt-6 grid gap-4">
          <input data-testid="catalogue-gate-name" required className="field" placeholder="Full name *" value={form.name} onChange={set("name")} />
          <input data-testid="catalogue-gate-email" required type="email" className="field" placeholder="Business email *" value={form.email} onChange={set("email")} />
          <input data-testid="catalogue-gate-company" className="field" placeholder="Company" value={form.company} onChange={set("company")} />
          <select data-testid="catalogue-gate-country" required className="field" value={form.country} onChange={set("country")}>
            <option value="">Country / region *</option>
            {REGIONS.map((r) => <option key={r.code} value={r.name}>{r.name}</option>)}
            <option value="Other">Other</option>
          </select>
          <button type="submit" data-testid="catalogue-gate-submit" disabled={mutation.isPending} className="btn-pill btn-navy mt-2 justify-center text-sm disabled:opacity-60">
            {mutation.isPending ? <><Loader2 size={16} className="animate-spin" /> Preparing</> : <><FileDown size={16} /> Download PDF</>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
